// themeSettings.js — dark mode + theme (primary color) presets, localStorage persist (S59).
// Trick: poora panel `cyan` palette + primaryColor 'cyan' use karta hai (index.js mein cyan ke
// naam pe ORANGE bhara tha). Theme change = bas `colors.cyan` mein chuna hua palette daal do —
// kisi page ko chhuna nahi padta.
import { createContext, useContext } from "react";
import { DEFAULT_THEME } from "@mantine/core";

const M = DEFAULT_THEME.colors;

// Har preset: label (UI, English) + swatch (picker dot) + 10-shade palette
export const THEME_PRESETS = {
  orange: {
    label: "Sunset Orange",
    swatch: "#F25016",
    colors: [
      "#FFF4EF", "#FFE4D9", "#FFC2A8", "#FFA073", "#FF7A42",
      "#F25016", // main
      "#D64514", "#B83A11", "#922E0D", "#6B2109",
    ],
  },
  blue:   { label: "Ocean Blue",   swatch: M.blue[6],   colors: M.blue },
  teal:   { label: "Teal",         swatch: M.teal[6],   colors: M.teal },
  green:  { label: "Forest Green", swatch: M.green[7],  colors: M.green },
  violet: { label: "Royal Violet", swatch: M.violet[6], colors: M.violet },
  pink:   { label: "Rose Pink",    swatch: M.pink[6],   colors: M.pink },
};

const SCHEME_KEY = "ui_color_scheme"; // "light" | "dark"
const PRESET_KEY = "ui_theme_preset"; // THEME_PRESETS ki key ya "custom"
const CUSTOM_KEY = "ui_theme_custom_hex"; // user ka apna chuna hua color

export const loadColorScheme = () =>
  localStorage.getItem(SCHEME_KEY) === "dark" ? "dark" : "light";
export const saveColorScheme = (scheme) => localStorage.setItem(SCHEME_KEY, scheme);

export const loadThemePreset = () => {
  const p = localStorage.getItem(PRESET_KEY);
  return THEME_PRESETS[p] || p === "custom" ? p : "orange";
};
export const saveThemePreset = (preset) => localStorage.setItem(PRESET_KEY, preset);

export const loadCustomHex = () => localStorage.getItem(CUSTOM_KEY) || "#F25016";
export const saveCustomHex = (hex) => localStorage.setItem(CUSTOM_KEY, hex);

// ── Custom color → 10-shade Mantine palette (user "multi color" ask, S59) ──
// index 5 = user ka color; 0-4 white ki taraf (light shades), 6-9 black ki taraf (dark shades).
const clampC = (n) => Math.max(0, Math.min(255, Math.round(n)));
const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  const f = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(f, 16);
  if (Number.isNaN(n) || f.length !== 6) throw new Error("bad hex");
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgbToHex = (rgb) => "#" + rgb.map((v) => clampC(v).toString(16).padStart(2, "0")).join("");
const mix = (rgb, target, w) => rgb.map((c, i) => c + (target[i] - c) * w);

export const generatePalette = (hex) => {
  try {
    const base = hexToRgb(hex);
    const lightW = [0.92, 0.82, 0.64, 0.42, 0.2];
    const darkW = [0.12, 0.26, 0.4, 0.54];
    return [
      ...lightW.map((w) => rgbToHex(mix(base, [255, 255, 255], w))),
      rgbToHex(base),
      ...darkW.map((w) => rgbToHex(mix(base, [0, 0, 0], w))),
    ];
  } catch {
    return THEME_PRESETS.orange.colors; // corrupt hex → default, kabhi crash nahi
  }
};

// Active 10-shade palette — preset ya custom hex se
export const getActivePalette = (preset, customHex) =>
  preset === "custom"
    ? generatePalette(customHex)
    : (THEME_PRESETS[preset] || THEME_PRESETS.orange).colors;

// Context — DashboardLayout ka dropdown isi se toggle/preset/custom badalta hai
export const ThemeSettingsContext = createContext({
  colorScheme: "light",
  toggleColorScheme: () => {},
  preset: "orange",
  setPreset: () => {},
  customHex: "#F25016",
  setCustomColor: () => {},
});

export const useThemeSettings = () => useContext(ThemeSettingsContext);
